// @ts-check
// "n de total" e anterior/próximo no cabeçalho da página do registro (doc 10, seção 1.4), pela lista de onde o
// registro foi aberto. Atalhos como os do Twenty no menu de comandos: Alt+K anterior e Alt+J próximo.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { findManyRecords } from '../../data/rest.js';
import { appendRecordListPage, computeRecordNavigation } from '../../lib/record-navigation.js';
import { LightIconButton } from '../../ui/button.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { openRecord, recordNavigationState, setRecordNavigationContext } from '../side-panel/open-record.js';
import { t } from '../../i18n/index.js';

const NEXT_PAGE_SIZE = 60;

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, recordId: string }} props
 */
export function RecordNavigationControls({ objectMetadata, recordId }) {
  const context = recordNavigationState.value;
  const navigation = computeRecordNavigation(context, objectMetadata.nameSingular, recordId);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const go = (/** @type {string} */ targetId) => openRecord({ objectNameSingular: objectMetadata.nameSingular, recordId: targetId, openIn: 'RECORD_PAGE' });

  const goNext = async () => {
    if (!navigation || !context) return;
    if (navigation.nextId) {
      go(navigation.nextId);
      return;
    }
    if (!navigation.needsMoreForNext || !context.endCursor || isLoadingMore) return;
    const object = objectsByNameSingular.value.get(context.objectNameSingular);
    if (!object) return;
    setIsLoadingMore(true);
    try {
      const page = await findManyRecords(object, {
        filter: context.filter, orderBy: context.orderBy ?? [], limit: NEXT_PAGE_SIZE, depth: 0, startingAfter: context.endCursor, drilldown: context.drilldown ?? null,
      });
      const next = appendRecordListPage(context, page.records.map((record) => record.id), {
        hasNextPage: Boolean(page.pageInfo?.hasNextPage), endCursor: page.pageInfo?.endCursor ?? null,
      });
      setRecordNavigationContext(next);
      const refreshed = computeRecordNavigation(next, objectMetadata.nameSingular, recordId);
      if (refreshed?.nextId) go(refreshed.nextId);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!navigation) return undefined;
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (!event.altKey || event.metaKey || event.ctrlKey) return;
      const target = /** @type {HTMLElement} */ (event.target);
      if (target.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (event.code === 'KeyK' && navigation.previousId) {
        event.preventDefault();
        go(navigation.previousId);
      } else if (event.code === 'KeyJ') {
        event.preventDefault();
        goNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigation?.previousId, navigation?.nextId, navigation?.needsMoreForNext, recordId]);

  if (!navigation || navigation.total <= 1) return null;
  const label = context?.groupLabel
    ? t('recordPage.navigation.positionInGroup', { rank: navigation.rank, total: navigation.total, group: context.groupLabel })
    : t('recordPage.navigation.position', { rank: navigation.rank, total: navigation.total });
  return html`<span class="record-navigation" data-testid="record-navigation">
    <span class="record-navigation-label">${label}</span>
    <${LightIconButton} icon="IconChevronUp" accent="tertiary" ariaLabel=${t('recordPage.navigation.previous')} disabled=${!navigation.previousId}
      onClick=${() => navigation.previousId && go(navigation.previousId)} />
    <${LightIconButton} icon="IconChevronDown" accent="tertiary" ariaLabel=${t('recordPage.navigation.next')}
      disabled=${!navigation.nextId && !navigation.needsMoreForNext} onClick=${goNext} />
  </span>`;
}
